import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const refreshCount = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count || 0);
    } catch { /* user not authed */ }
  };

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 25000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const openPanel = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      try {
        const { data } = await api.get("/notifications", { params: { limit: 20 } });
        setItems(data);
      } finally { setLoading(false); }
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setCount(0);
    } catch { /* ignore */ }
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="nav-notifications-btn"
        onClick={openPanel}
        className="relative p-2 text-gray-700 hover:text-gray-900 transition-colors"
        aria-label="Notifiche"
      >
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#E11D48] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white border border-gray-200 rounded-sm shadow-2xl z-50 max-h-[480px] overflow-hidden flex flex-col" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <span className="label-tag">Notifiche</span>
            {count > 0 && (
              <button onClick={markAll} className="text-[10px] uppercase tracking-widest text-[#E11D48] hover:text-gray-900" data-testid="notification-mark-all">
                Segna lette
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-6 text-center text-xs text-gray-500">Caricamento...</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">Nessuna notifica</div>
            ) : (
              items.map((n) => (
                <Link
                  key={n.notification_id}
                  to={n.ride_id ? `/ride/${n.ride_id}` : "/feed"}
                  data-testid={`notification-${n.notification_id}`}
                  onClick={() => setOpen(false)}
                  className={`block px-4 py-3 border-b border-gray-100 hover:bg-gray-100 transition-colors ${n.read ? "" : "bg-[#E11D48]/[0.05]"}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#E11D48] mt-2 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900 leading-tight">{n.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate">{n.body}</div>
                      <div className="text-[10px] text-gray-400 mt-1 uppercase tracking-widest">
                        {format(new Date(n.created_at), "d MMM · HH:mm", { locale: it })}
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
